import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { ELECTIONS } from "@/data/content";

const diff = (iso) => {
  const ms = Math.max(new Date(iso).getTime() - Date.now(), 0);
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return { days, hours, minutes, seconds };
};

function Digit({ value, label }) {
  const v = String(value).padStart(2, "0");
  return (
    <div className="text-center">
      <div className="relative h-14 overflow-hidden sm:h-20">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={v}
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "-100%", opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="block font-display text-4xl font-bold tabular-nums text-paper sm:text-6xl"
          >
            {v}
          </motion.span>
        </AnimatePresence>
      </div>
      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
    </div>
  );
}

export default function ElectionCountdown() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="border-t border-white/10 bg-coal/40" data-testid="election-countdown-section">
      <div className="mx-auto max-w-[1440px] px-6 py-16 lg:px-12 lg:py-20">
        <FadeUp>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-brand">Election Clock — चुनावी घड़ी</p>
              <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-3xl">
                Every day you wait, the opposition gains a booth.
              </h2>
            </div>
            <Link
              to="/packages"
              data-testid="countdown-cta"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-brand"
            >
              Start your 90-day plan
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </FadeUp>
        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {ELECTIONS.map((e, idx) => {
            const d = diff(e.date);
            return (
              <FadeUp key={e.id} delay={idx * 0.1}>
                <div className="rounded-2xl border border-white/10 bg-ink/60 p-6 lg:p-8" data-testid={`countdown-${e.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-display text-xl font-bold uppercase tracking-tight text-paper">{e.name}</p>
                      <p className="font-hindi text-base text-saffron">{e.hindi}</p>
                    </div>
                    <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">{e.note}</span>
                  </div>
                  <div className="mt-6 grid grid-cols-4 gap-3">
                    <Digit value={d.days} label="Days" />
                    <Digit value={d.hours} label="Hours" />
                    <Digit value={d.minutes} label="Min" />
                    <Digit value={d.seconds} label="Sec" />
                  </div>
                </div>
              </FadeUp>
            );
          })}
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Dates indicative, based on the previous election cycle. Final schedule by the Election Commission of India.</p>
      </div>
    </section>
  );
}
